import {
  CurrentUserRequirement,
  ProjectStateFilter,
  ProjectStateFilterType,
} from "./ProjectStateFilter";
import ProjectOverview from "@/data/models/ProjectOverview";
import { UserLookup } from "@/data/models/UserLookup";
import { ProjectStateInfo } from "@/data/models/ProjectState";
import User from "@/data/models/User";

interface MatchingProjectOverviewWithState {
  projectOverview: ProjectOverview;
  state: ProjectStateInfo | null;
}

export class ProjectStateFilterCounter {
  numMatchingProjects = 0;
  filter: ProjectStateFilter;
  storeMatchingProjectOverviews: boolean;
  matchingProjectOverviews: MatchingProjectOverviewWithState[] = [];

  constructor(
    projectStateFilterType: ProjectStateFilterType,
    userRequirements?: CurrentUserRequirement[],
    storeMatchingProjectOverviews?: boolean
  ) {
    this.filter = new ProjectStateFilter(
      projectStateFilterType,
      userRequirements
    );

    this.storeMatchingProjectOverviews = storeMatchingProjectOverviews ?? false;
  }

  visit(
    projectOverview: ProjectOverview,
    targetUser: User | null,
    userLookup: UserLookup,
    today: Date,
    projectState?: ProjectStateInfo | null
  ) {
    if (
      this.filter.matchesProjectOverview(
        projectOverview,
        targetUser,
        userLookup,
        today,
        {
          lastCompletedStep: projectState,
        }
      )
    ) {
      this.numMatchingProjects++;

      if (this.storeMatchingProjectOverviews) {
        this.matchingProjectOverviews.push({
          projectOverview,
          state: projectState ?? null,
        });
      }
    }
  }

  getDescription(vue: Vue): string {
    switch (this.filter.filterType) {
      case ProjectStateFilterType.OVERALL:
        return this.formatString(vue, "overall");
      case ProjectStateFilterType.ALL_PROJECTS:
        return this.formatString(vue, "allProjects");
      case ProjectStateFilterType.NEW_PROJECT:
      case ProjectStateFilterType.RELEVANT_NEW_PROJECTS:
        return this.formatString(vue, "new");
      case ProjectStateFilterType.LEAK_DETECTION:
        return this.formatString(vue, "leakDetection");
      case ProjectStateFilterType.DAMAGE_ASSESSMENT:
        return this.formatString(vue, "damageAssessment");
      case ProjectStateFilterType.DRYING:
        return this.formatString(vue, "drying");
      case ProjectStateFilterType.REPAIR:
        return this.formatString(vue, "repair");
      case ProjectStateFilterType.WITH_HY_DRY_DEVICES:
        return this.formatString(vue, "withHyDryDevices");

      case ProjectStateFilterType.MISSING_PROJECT_APPROVAL:
        return this.formatString(vue, "missingProjectApproval");
      case ProjectStateFilterType.MISSING_FIRST_CONTACT:
        return this.formatString(vue, "missingFirstContact");
      case ProjectStateFilterType.INSURANCE_OFFER:
        return this.formatString(vue, "insuranceOffer");
        case ProjectStateFilterType.MISSING_DRYING_APPROVAL_PROJECTORGANISATION:  
      case ProjectStateFilterType.MISSING_DRYING_APPROVAL:
        return this.formatString(vue, "missingDryingApproval");
      case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION:
      case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_PROJECTORGANISATION:
      case ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_NO_APP:
        return this.formatString(vue, "missingProjectConclusion");

      case ProjectStateFilterType.MAKE_LEAK_DETECTION_APPOINTMENT:
        return this.formatString(vue, "leakDetection");
      case ProjectStateFilterType.MAKE_DAMAGE_ASSESSMENT_APPOINTMENT:
        return this.formatString(vue, "damageAssessment");
      case ProjectStateFilterType.MAKE_SETUP_DRYING_APPOINTMENT:
        return this.formatString(vue, "dryingSetup");
      case ProjectStateFilterType.MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT:
        return this.formatString(vue, "intermediateMeasurement");
      case ProjectStateFilterType.MAKE_DRYING_CONCLUSION_APPOINTMENT:
        return this.formatString(vue, "dryingConclusion");

      case ProjectStateFilterType.PROJECT_OWNER:
        return this.formatString(vue, "ownProjects");
      case ProjectStateFilterType.PROJECT_SUPERVISOR:
        return this.formatString(vue, "projectSupervisor");
      case ProjectStateFilterType.FOLLOW_UP_INSPECTION:
        return this.formatString(vue, "followUpInspection");
      case ProjectStateFilterType.OVERDUE_RELEVANT_PROJECTS:
      case ProjectStateFilterType.OVERDUE_MY_PROJECTS:
      case ProjectStateFilterType.OVERDUE:
        return this.formatString(vue, "overdue");
      case ProjectStateFilterType.RECENTLY_FINISHED:
      case ProjectStateFilterType.RECENTLY_FINISHED_RELEVANT_PROJECTS:
        return this.formatString(vue, "recentlyFinished");
      case ProjectStateFilterType.MISSING_REPORT:
        return this.formatString(vue, "missingReport");

      case ProjectStateFilterType.MISSING_PROJECT_OWNER:
        return this.formatString(vue, "missingProjectOwner");

      case ProjectStateFilterType.ASSIGN_DEVICES:
        return this.formatString(vue, "assignDevices");
    }

    return "TODO";
  }

  private formatString(vue: Vue, i18nRes: string): string {
    return vue
      .$tc(`projectStateFilter.${i18nRes}`, this.numMatchingProjects)
      .toString();
  }
}
